import { Link } from "react-router-dom";
import { sp } from "@/store/nav";
import { useCart } from "./cart";
import { ShieldCheck, PackageSearch, ShoppingCart } from "lucide-react";

import { BACKEND_URL as API } from "@/lib/api";

function Certainty({ level, st }) {
  const key = `cert_${level}`;
  const color = level === "confirmed" || level === "exact_pn" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : level === "confirmed_once" ? "bg-amber-50 text-amber-800 border-amber-200" : "bg-slate-50 text-slate-600 border-slate-200";
  return <span data-testid="chat-card-certainty" className={`inline-block rounded-full border px-2 py-0.5 text-[10px] font-medium ${color}`}>{st(key)}</span>;
}

export function ProductCard({ c, st }) {
  const { items, add } = useCart();
  const unit = c.units?.[0];
  const inCart = unit && items.some((i) => i.sku === unit.sku);
  return (
    <div data-testid={`chat-product-card-${c.slug}`} className="flex gap-3 rounded-xl border border-slate-200 bg-white p-2.5 shadow-sm">
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-slate-100">{c.photo ? <img src={`${API}/api/store/photos/${c.photo}?w=160`} alt="" className="h-full w-full object-cover" /> : <PackageSearch className="m-auto mt-5 h-6 w-6 text-slate-400" />}</div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold text-slate-900">{c.title}</div>
        <div className="font-mono text-[11px] text-slate-500">PN {c.part_number} · {c.available} {st("chat_avail")}</div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-bold text-emerald-700">${c.price_from}</span>
          <span className="text-[10px] text-slate-500">{c.conditions.join(", ")}</span>
          <Certainty level={c.match_certainty} st={st} />
        </div>
        <div className="mt-1.5 flex gap-2">
          <Link data-testid="chat-card-view" to={sp(`/p/${c.slug}`)} className="rounded-full border border-slate-300 px-3 py-1 text-[11px] font-semibold text-slate-700 hover:border-emerald-600 hover:text-emerald-700">{st("chat_view")}</Link>
          {unit && <button data-testid="chat-card-add" disabled={inCart} onClick={() => add({ sku: unit.sku, price: unit.price, condition: unit.condition, photos: c.photo ? [c.photo] : [] }, { title: c.title, part_number: c.part_number, slug: c.slug })} className="flex items-center gap-1 rounded-full bg-[#0f2a3f] px-3 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"><ShoppingCart className="h-3 w-3" />{inCart ? st("in_cart") : st("add")}</button>}
        </div>
      </div>
    </div>
  );
}

export function KnownPartCard({ c, st }) {
  return (
    <div data-testid={`chat-known-card-${c.part_number}`} className="rounded-xl border border-sky-200 bg-sky-50/60 p-2.5 text-[12px] text-slate-800">
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-sky-800"><ShieldCheck className="h-3 w-3" />{st("chat_known")}</div>
      <div className="mt-0.5 font-semibold">{c.name || c.category}{c.brand ? ` · ${c.brand}` : ""}</div>
      <div className="font-mono text-[11px]">OEM PN {c.part_number}</div>
      <div className="mt-1 text-[11px] font-medium text-amber-800">{st("chat_not_in_stock")}</div>
      {c.replacements?.length > 0 && <div className="mt-1 text-[11px]"><span className="text-slate-500">{st("chat_replacements")}:</span> <span className="font-mono">{c.replacements.join(", ")}</span></div>}
      {c.models?.length > 0 && <div className="mt-1 flex flex-wrap gap-1 text-[11px]"><span className="text-slate-500">{st("chat_fits")}:</span>{c.models.map((m) => <span key={m.model} className="rounded border border-slate-200 bg-white px-1.5 font-mono">{m.model} <span className="text-slate-400">· {st(`cert_${m.certainty}`) || m.certainty}</span></span>)}</div>}
    </div>
  );
}

export function Cards({ cards, st }) {
  if (!cards?.length) return null;
  return <div className="space-y-2" data-testid="chat-cards">{cards.map((c, i) => (c.kind === "product" ? <ProductCard key={i} c={c} st={st} /> : <KnownPartCard key={i} c={c} st={st} />))}</div>;
}
